// src/components/Calendar/ScheduleModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, MenuItem, FormControlLabel, Checkbox,
    Stack, Tabs, Tab, Box, useMediaQuery, useTheme,
} from '@mui/material';
import {
    LocalizationProvider,
    DatePicker,
    TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import CalendarVoteForm from './CalendarVoteForm';

const EVENT_TYPES = ['투표벙', '갠벙', '플벙', '정모'];
const CONTENT_COLORS = [
    { label: '파랑', value: '#1976d2' },
    { label: '초록', value: '#2e7d32' },
    { label: '보라', value: '#6a1b9a' },
    { label: '회색', value: '#616161' },
    { label: '빨강', value: '#d81b60' },
    { label: '주황', value: '#f57c00' },
];

export default function ScheduleModal({
    open,
    onClose,
    selectedDate,
    onAdd,
    isEdit = false,
    initialData = null,
    isAdmin = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [tab, setTab] = useState(0);

    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs().add(1, 'hour'));
    const [startTime, setStartTime] = useState(dayjs().hour(19).minute(0));
    const [endTime, setEndTime] = useState(dayjs().hour(21).minute(0));
    const [eventType, setEventType] = useState('');
    const [isUrgent, setIsUrgent] = useState(false);
    const [people, setPeople] = useState('');
    const [location, setLocation] = useState('');

    const [contentName, setContentName] = useState('');
    const [color, setColor] = useState('#1976d2');

    const [voteData, setVoteData] = useState(null);

    useEffect(() => {
        if (isEdit && initialData) {
            const start = dayjs(initialData.date);
            const end = dayjs(initialData.endDate || initialData.date);

            setStartDate(start);
            setEndDate(end);
            setStartTime(dayjs(`${initialData.date}T${initialData.time}`));
            setEndTime(dayjs(`${initialData.endDate || initialData.date}T${initialData.endTime || initialData.time}`));

            setEventType(initialData.type || '');
            setIsUrgent(initialData.urgent || false);
            setPeople(initialData.people || '');
            setLocation(initialData.location || '');
            setContentName(initialData.title || '');
            setColor(initialData.color || '#1976d2');

            if (initialData.vote) {
                setVoteData(initialData.vote);
            }
        } else if (selectedDate) {
            const base = dayjs(selectedDate);
            setStartDate(base);
            setEndDate(base);
            setStartTime(base.hour(19).minute(0));
            setEndTime(base.hour(21).minute(0));

            setEventType('');
            setIsUrgent(false);
            setPeople('');
            setLocation('');
            setContentName('');
            setColor('#1976d2');
            setVoteData(null);
        }
    }, [initialData, selectedDate, isEdit]);

    const handleSubmit = () => {
        if (
            (tab === 0 && (!eventType || !people.trim() || !location.trim())) ||
            (tab === 1 && (!contentName.trim() || !location.trim()))
        ) {
            alert('모든 항목을 입력해주세요!');
            return;
        }

        const hasVote = voteData?.has_vote === true;
        const hasVoteRecord = voteData?.has_vote_record === true;

        if (hasVote && !hasVoteRecord) {
            const q = voteData?.vote_question?.trim() ?? '';
            const opts = voteData?.vote_options ?? [];
            if (!q || opts.length < 2) {
                alert('투표 질문과 최소 두 개의 선택지를 입력해주세요!');
                return;
            }
        }

        const common = {
            date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            time: startTime.format('HH:mm'),
            end_time: endTime.format('HH:mm'),
            location,
        };

        let payload =
            tab === 0
                ? { ...common, type: eventType, urgent: isUrgent, people }
                : { ...common, title: contentName, content_type: 'internal', color };

        if (hasVote) {
            const votePayload = voteData.has_vote_record
                ? {
                    deadline: voteData.vote_deadline,  // ✅ 마감시간만 보냄
                }
                : {
                    question: voteData.vote_question.trim(),
                    options: voteData.vote_options,
                    deadline: voteData.vote_deadline,
                    allow_multiple: voteData.allow_multiple,
                    is_anonymous: voteData.is_anonymous,
                    allow_add_option: voteData.allow_add_option,
                };

            payload = {
                ...payload,
                vote: votePayload,
            };
        }

        const id = isEdit && initialData?.id ? initialData.id : null;
        onAdd(payload, isEdit, id);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? '✏️ 일정 수정' : '📌 일정 추가'}</DialogTitle>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
                <Tab label="일정" />
                {isAdmin && <Tab label="컨텐츠" />}
            </Tabs>

            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2} mt={1}>
                        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                            <DatePicker
                                label="시작 날짜"
                                value={startDate}
                                onChange={(d) => {
                                    setStartDate(d);
                                    if (d.isAfter(endDate)) setEndDate(d);
                                }}
                                format="YYYY/MM/DD"
                                sx={{ flex: 1 }}
                            />
                            <TimePicker
                                label="시작 시간"
                                value={startTime}
                                onChange={setStartTime}
                                sx={{ flex: 1 }}
                            />
                        </Stack>

                        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                            <DatePicker
                                label="종료 날짜"
                                value={endDate}
                                onChange={setEndDate}
                                minDate={startDate}
                                format="YYYY/MM/DD"
                                sx={{ flex: 1 }}
                            />
                            <TimePicker
                                label="종료 시간"
                                value={endTime}
                                onChange={setEndTime}
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                    </Stack>
                </LocalizationProvider>

                {tab === 0 ? (
                    <Stack spacing={2} mt={3}>
                        <TextField
                            select
                            label="벙 제목"
                            value={eventType}
                            onChange={(e) => setEventType(e.target.value)}
                            fullWidth
                        >
                            {EVENT_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isUrgent}
                                    onChange={(e) => setIsUrgent(e.target.checked)}
                                />
                            }
                            label="급벙"
                        />

                        <TextField
                            label="인원"
                            value={people}
                            onChange={(e) => setPeople(e.target.value)}
                            fullWidth
                            placeholder="ex) 쿠야 외 3인"
                        />
                        <TextField
                            label="장소"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            fullWidth
                            placeholder="ex) 강남역 11번 출구"
                        />
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={3}>
                        <TextField
                            label="컨텐츠명"
                            value={contentName}
                            onChange={(e) => setContentName(e.target.value)}
                            fullWidth
                            placeholder="ex) 수러팅 / 우성우"
                        />
                        <TextField
                            label="장소"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            fullWidth
                            placeholder="ex) 보이스룸 / 산들 채팅방"
                        />
                        <TextField
                            select
                            label="색상 선택"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            fullWidth
                        >
                            {CONTENT_COLORS.map((c) => (
                                <MenuItem key={c.value} value={c.value}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            backgroundColor: c.value,
                                            mr: 1,
                                        }}
                                    />
                                    {c.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                )}

                <Box mt={2}>
                    <CalendarVoteForm
                        initialVoteData={voteData || undefined}
                        onChange={setVoteData}
                        isEdit={Boolean(voteData)}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {isEdit ? '수정' : '등록'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
